<template>
  <v-form ref="form" v-model="valid" lazy-validation>
    <template v-if="isUpdate && !overWriteTaskGroup">
      <dl class="dl">
        <dt>Assessment Modifier</dt>
        <dd v-if="user.assessment_duration_modifier !== null">
          {{ user.assessment_duration_modifier }}
        </dd>
        <dd v-else><em>Multiple Values</em></dd>

        <dt>Time Slot Selection</dt>
        <dd v-if="user.assessment_skip_scheduling !== null">
          {{ user.assessment_skip_scheduling ? 'Optional' : 'Required' }}
        </dd>
        <dd v-else><em>Multiple Values</em></dd>

        <dt>Scheduling From</dt>
        <dd v-if="user.assessment_start_date !== null">
          {{ user.assessment_start_date | dateTime }}
        </dd>
         <dd v-else-if="user.assessment_date === null && user.isAssessmentStartDateNullForAll">
          Now
        </dd>
        <dd v-else><em>Multiple Values</em></dd>

        <dt>Scheduling To</dt>
        <dd v-if="user.assessment_end_date !== null">
          {{ user.assessment_end_date | dateTime }}
        </dd>
        <dd v-else><em>Multiple Values</em></dd>

        <dt>Login Window Selected</dt>
        <dd v-if="user.assessment_date !== null">
          {{ user.assessment_date | dateTime }}
        </dd>
        <dd v-else-if="user.assessment_date === null && user.isAssessmentDateNullForAll">
          -
        </dd>
        <dd v-else><em>Multiple Values</em></dd>
      </dl>
    </template>
    <v-layout wrap row class="mt-3" v-else>
      <v-flex xs6 class="pl-4">
        <v-card class="ma-1">
          <v-toolbar flat dense class="pr-0">
            <v-toolbar-title>Set Login Window Availiabilities</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <skip-scheduling-field v-model="user.assessment_skip_scheduling"
                                   :force-default-value="(!isUpdate) || isUpdate"
                                   :user-type="user.user_type" />
            <date-field v-if="!user.assessment_skip_scheduling"
                        v-model="user.assessment_start_date"
                        :location="user.assessment_location"
                        :force-default-value="isCreate || isNewAssessment"
                        title="Scheduling Available From"
                        instructions="Select first login window users can choose" />
            <date-field v-if="!user.assessment_skip_scheduling"
                        v-model="user.assessment_end_date"
                        :location="user.assessment_location"
                        :force-default-value="isCreate || isNewAssessment"
                        :defaultOffset="{weeks:2}"
                        title="Scheduling Available Until"
                        instructions="Select last login window users can choose"/>

          </v-card-text>
        </v-card>
      </v-flex>
      <v-flex xs12 class="px-4 mt-3">
        <v-btn v-if="!showAdvanced" @click="openAdvanced" color="primary darken-2 mb-4" data-test="advancedConfigurations">
          <v-icon>layers</v-icon>
          Advanced Configurations
        </v-btn>
        <v-layout v-if="showAdvanced" wrap row>
          <v-flex xs6 pl-3>
            <v-card v-if="isUpdate && $can({ schedule: 'read' })" class="ma-1" :class="{ 'opacity-5': user.assessment_skip_scheduling }">
              <v-toolbar flat dense class="pr-0">
                <v-toolbar-title>Override Login Window</v-toolbar-title>
              </v-toolbar>
              <v-card-text>
                <assessment-date-field v-model="user.assessment_date"
                                      :required-slots-count="usersCount"
                                      :location="user.assessment_location"
                                      :disabled="user.assessment_skip_scheduling"/>
              </v-card-text>
            </v-card>
            <v-card v-if="$can({ admin: 'write'})" class="ma-1">
              <v-toolbar flat dense>
                <v-toolbar-title>Service Version Selector</v-toolbar-title>
              </v-toolbar>
              <v-card-text>
                <p>Select a service version from the dropdown:</p>
                <v-select
                  v-model="user.client_version_id"
                  :items="serviceVersionsItems"
                  label="Available Service Versions"
                  item-text="version"
                  item-value="id"
                  >
                  <template v-slot:item="{ item }">
                    <div>
                      <p class="subheading font-weight-bold ma-0">{{ item.version }}</p>
                      <p v-if="item.image_tag" class="body-2 font-weight-light font-italic d-block ma-0">sha: {{ item.image_tag }}</p>
                    </div>
                  </template>
                </v-select>
              </v-card-text>
            </v-card>
          </v-flex>
          <v-flex xs6>
            <v-card class="ma-1 pr-4 mr-4" v-if="this.data.updateType !== 'retest'">
              <v-toolbar flat dense>
                <v-toolbar-title>Assessment Modifier</v-toolbar-title>
              </v-toolbar>
              <v-card-text>
                <assessment-modifier-field v-model="user.assessment_duration_modifier" :taskIds="taskIds" />
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
    <v-layout>
      <v-flex pt-5 mt-1>
        <v-toolbar color="white" flat>
          <v-spacer></v-spacer>
          <template v-if="isUpdate && !overWriteTaskGroup" class="">
            <v-btn color="primary darken-1" @click="overWriteTaskGroup = true" class="pl-1" data-test="changeAssessmentConfiguration">
              <v-icon class="px-1 ml-2" left>edit</v-icon>
              Change Scheduling
            </v-btn>
          </template>
          <div class="ml-2">
            <v-btn v-if="isUpdate && this.data.updateType === 'override' && canUndo"
                   color="secondary darken-2"
                   @click="undoAll">
              <v-icon class="px-1" left>refresh</v-icon>
              Reset
            </v-btn>
          </div>
          <v-btn color="success darken-2"
                 @click="next"
                 class="pl-4"
                 data-test="finishAssessmentConfig">
            <v-icon class="px-1" left>check</v-icon>
            Next
          </v-btn>
        </v-toolbar>
      </v-flex>
    </v-layout>
    <confirm-dialog ref="confirm" @confirm="goToNext">
      <p class="mt-1 mb-0" v-if="isSkipSchedulingConfirm">- This would <strong>skip the time slot selection</strong> step.</p>
      <p class="mt-3 mb-0">Are you sure this is correct?</p>
    </confirm-dialog>
  </v-form>
</template>

<script>
import { mapState } from 'vuex'
import FormStep from '@/mixins/FormStep'
import AssessmentModifierField from '@/components/Form/AssessmentModifierField'
import SkipSchedulingField from '@/components/Form/SkipSchedulingField'
import DateField from '@/components/Form/DateField'
import AssessmentDateField from '@/components/Form/AssessmentDateField'
import { config } from '@/helpers'
import ConfirmDialog from '@/components/ConfirmDialog'
import UndoRedoMixin from '@/mixins/UndoRedo'

export default {
  name: 'AccountScheduling',
  components: { AssessmentDateField, DateField, SkipSchedulingField, AssessmentModifierField, ConfirmDialog },
  mixins: [ FormStep, UndoRedoMixin ],
  data () {
    return {
      config: config,
      overWriteTaskGroup: false,
      user: {
        assessment_skip_scheduling: null,
        assessment_duration_modifier: 1.0,
        assessment_start_date: null,
        assessment_end_date: null,
        assessment_date: null,
        client_version_id: null
      },
      showAdvanced: false
    }
  },
  computed: {
    ...mapState('assessmentConfigs', {
      assessmentConfigs: 'assessmentConfigs'
    }),
    ...mapState('clientServiceVersions', {
      serviceVersions: 'serviceVersions'
    }),
    serviceVersionsItems () {
      return [{ id: null, version: 'Default' }].concat(Object.values(this.serviceVersions))
    },
    /**
     *  Is adding new assessment to existed account
     */
    isNewAssessment () {
      return this.isUpdate && (this.data.updateType === 'choose')
    },
    /**
     * Base duration for these task groups which will be passed to AssessmentModifierField
     *
     * @returns {*}
     */
    taskIds () {
      let tasks = []

      if (this.data.customConfig && this.data.customConfig.tasks && this.data.customConfig.tasks.length) {
        tasks = this.data.customConfig.tasks.names
      } else {
        // either common task group passed in the update flow or existing task group (override in update or create flow)
        const assessmentConfigId = this.data.commonAssessmentConfigId || this.data.assessmentConfigId
        if (assessmentConfigId) {
          const assessmentConfig = this.assessmentConfigs[assessmentConfigId]
          if (assessmentConfig) {
            tasks = assessmentConfig.config.tasks.names
          }
        }
      }
      var tasksId = tasks.map((taskName) => {
        return this.$store.getters['tasks/taskTypeByName'](taskName).id
      })
      return tasksId
    },
    /**
     * If true, the user type is candidate
     *
     * @returns {boolean}
     */
    isCandidateAccount () {
      return this.data.user.user_type === 'Candidate'
    },
    /**
     * If true, the user asks to skip scheduling for a candidate account: we will ask for confirmation
     *
     * @returns {boolean}
     */
    isSkipSchedulingConfirm () {
      return this.user.assessment_skip_scheduling && this.isCandidateAccount
    },
    /**
     * Get number of users
     */
    usersCount () {
      if (!('users' in this.data)) {
        return 1
      }
      return (this.data.users.length === 0)
        ? 1
        : this.data.users.length
    }
  },
  methods: {
    entered () {
      if ('user' in this.data) {
        Object.keys(this.data.user).forEach(key => {
          this.$set(this.user, key, this.data.user[key])
        })
      }

      if (this.isNewAssessment && !this.overWriteTaskGroup) {
        this.overWriteTaskGroup = true
        this.clearFields()
      }
    },
    formData () {
      if (this.user.assessment_skip_scheduling === true) {
        this.$set(this.user, 'assessment_date', null)
      }

      return {
        user: this.user
      }
    },
    goToNext () {
      if (this.isSkipSchedulingConfirm) {
        this.$refs.confirm.shown = true
      } else {
        this.next()
      }
    },
    /**
     * Clear assessment config fields
     */
    clearFields () {
      [
        'assessment_skip_scheduling',
        'assessment_duration_modifier',
        'assessment_start_date',
        'assessment_end_date',
        'assessment_date',
        'client_version_id'
      ].forEach(field => {
        this.$set(this.user, field, null)
      })
    },
    /**
     * Get active service versions for game-ui
     */
    openAdvanced () {
      this.showAdvanced = true
      return this.$store.dispatch('clientServiceVersions/getServiceVersions', 'game-ui')
    }
  }
}
</script>
<style lang="sass">
.dl
  display: grid
  grid-template-columns: max-content auto
</style>
